import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../utils/apiConfig';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email) {
            toast.error('Please enter your email address');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to request password reset');
            }

            setEmailSent(true);
            toast.success('Password reset email sent');
        } catch (error: any) {
            console.error('Forgot password error:', error);
            toast.error(error.message || 'Failed to request password reset');
        } finally {
            setLoading(false);
        }
    };

    if (emailSent) {
        return (
            <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 font-sans">
                <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl shadow-zinc-200 w-full max-w-md text-center border border-zinc-100">
                    <div className="w-20 h-20 bg-[#AAD816]/10 rounded-3xl mx-auto mb-6 flex items-center justify-center">
                        <svg className="w-10 h-10 text-[#AAD816]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-3xl font-black text-zinc-950 uppercase tracking-tight mb-2">Check your email</h2>
                    <p className="text-zinc-500 font-medium mb-8 leading-relaxed">
                        We've sent password reset instructions to <br />
                        <strong className="text-zinc-900 font-bold">{email}</strong>
                    </p>
                    <div className="space-y-4">
                        <Link
                            to="/login"
                            className="block w-full bg-zinc-950 hover:bg-zinc-900 text-white font-black uppercase tracking-widest py-4 px-4 rounded-2xl transition-all active:scale-[0.98] shadow-lg shadow-zinc-200"
                        >
                            Back to Sign In
                        </Link>
                        <button
                            onClick={() => setEmailSent(false)}
                            className="text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-950 transition-colors pt-4"
                        >
                            Did not receive the email? Try again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 font-sans">
            <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl shadow-zinc-200 w-full max-w-md border border-zinc-100">
                <div className="text-center mb-10">
                    <div className="flex justify-center mb-10">
                        <img src="/gympe.svg" alt="Gympe" className="h-10 w-auto" />
                    </div>
                    <div className="w-16 h-16 bg-zinc-950 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl shadow-zinc-200">
                        <svg className="w-8 h-8 text-[#AAD816]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                    </div>
                    <h2 className="text-3xl font-black text-zinc-950 uppercase tracking-tight">Reset access</h2>
                    <p className="text-zinc-500 font-medium mt-3 leading-relaxed">Enter your email address and we'll send you instructions to reset your password.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="space-y-2">
                        <label htmlFor="email" className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-1">
                            Email address
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium outline-none"
                            placeholder="Enter your email"
                            disabled={loading}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-zinc-950 hover:bg-zinc-900 text-white font-black uppercase tracking-widest py-4 px-4 rounded-2xl transition-all active:scale-[0.98] shadow-lg shadow-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <div className="w-4 h-4 border-2 border-[#AAD816] border-t-transparent rounded-full animate-spin mr-3"></div>
                                Sending...
                            </div>
                        ) : (
                            'Send Instructions'
                        )}
                    </button>

                    <div className="text-center pt-2">
                        <Link to="/login" className="text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-950 transition-colors">
                            Back to Sign In
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
